// EnhancedQuickStats.js - Fixed to show consistent stats regardless of filters
import React from 'react';
import { Calendar, Filter, Users, User, Clock, AlertCircle, CheckCircle, FileText } from 'lucide-react';

const EnhancedQuickStats = ({ 
  timeEntries, 
  visibleEntries, 
  filters, 
  viewMode, 
  currentDate, 
  userRole,
  teamViewMode = 'team', 
  onStatClick
}) => {
  // Helper function to format time
  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // Helper function to describe the current time period
  const getPeriodDescription = () => {
    const today = new Date();
    const current = new Date(currentDate);
    
    if (viewMode === 'day') {
      if (current.toDateString() === today.toDateString()) {
        return 'Today';
      } else {
        return current.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'short', 
          day: 'numeric' 
        });
      }
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(current);
      startOfWeek.setDate(current.getDate() - current.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else if (viewMode === 'month') {
      return current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else {
      return 'All Entries';
    }
  };

  // IMPORTANT: Only totalVisibleTime uses visibleEntries (filtered results)
  // All other stats use timeEntries (unfiltered) so they don't change when filters are applied
  const totalVisibleTime = visibleEntries.reduce((sum, entry) => sum + entry.duration_hours, 0);
  
  // Status counts from ALL timeEntries (not filtered) - these should stay constant
  const draftEntries = timeEntries.filter(entry => entry.status === 'draft').length;
  const pendingEntries = timeEntries.filter(entry => entry.status === 'submitted' || entry.status === 'in_review').length;
  const approvedEntries = timeEntries.filter(entry => entry.status === 'approved').length;

  // Needs Approval (for admin/manager)
  const needsApproval = timeEntries.filter(e => 
    (e.status === 'submitted' || e.status === 'in_review') && 
    (userRole === 'admin' || userRole === 'manager')
  ).length;

  // Drafts >3 days old
  const oldDrafts = timeEntries.filter(e => {
    if (e.status !== 'draft') return false;
    const created = new Date(e.created_at || e.entry_date);
    const daysSince = (Date.now() - created) / (1000 * 60 * 60 * 24);
    return daysSince > 3;
  }).length;

  // Get active filters description
  const getActiveFiltersDescription = () => {
    const activeFilters = [];
    
    if (filters.search) {
      activeFilters.push(`Search: "${filters.search}"`);
    }
    if (filters.clientId && filters.clientId !== 'all') {
      activeFilters.push('Client filtered');
    }
    if (filters.projectId && filters.projectId !== 'all') {
      activeFilters.push('Project filtered');
    }
    if (filters.priority && filters.priority !== 'all') {
      activeFilters.push(`${filters.priority} priority`);
    }
    if (filters.status && filters.status !== 'all') {
      const statusLabel = filters.status === 'not_approved' ? 
                         'Excluding approved' : 
                         filters.status.charAt(0).toUpperCase() + filters.status.slice(1);
      activeFilters.push(statusLabel);
    }
    if (filters.billable && filters.billable !== 'all') {
      activeFilters.push(filters.billable === 'billable' ? 'Billable only' : 'Non-billable only');
    }
    if (filters.draftAge && filters.draftAge !== 'all') {
      activeFilters.push('Draft age filtered');
    }
    
    return activeFilters;
  };

  const periodDescription = getPeriodDescription();
  const activeFilters = getActiveFiltersDescription();
  const hasActiveFilters = activeFilters.length > 0;

  // Determine what text to show in the top right corner
  const getViewModeText = () => {
    if (userRole === 'admin' || userRole === 'manager') {
      if (teamViewMode === 'my') {
        return {
          icon: User,
          text: 'My Entries'
        };
      } else {
        return {
          icon: Users,
          text: 'All Team Members'
        };
      }
    } else {
      return {
        icon: User,
        text: 'Your Entries'
      };
    }
  };

  const viewModeInfo = getViewModeText();
  const ViewModeIcon = viewModeInfo.icon;

  // Helper to check if a stat is currently active based on filters
  const isStatActive = (statType) => {
    switch(statType) {
      case 'needs_approval':
      case 'pending':
        return filters.status === 'submitted' && (!filters.draftAge || filters.draftAge === 'all');
      case 'old_drafts':
        return filters.status === 'draft' && filters.draftAge === 'old';
      case 'drafts':
        return filters.status === 'draft' && (!filters.draftAge || filters.draftAge === 'all');
      case 'approved':
        return filters.status === 'approved';
      case 'total':
        return filters.status === 'all';
      default:
        return false;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      {/* Period and Filter Context Header */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-700">{periodDescription}</span>
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center space-x-2">
                <div className="w-1 h-4 bg-slate-300" />
                <Filter className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-blue-600 font-medium">
                  {activeFilters.join(', ')}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 text-xs text-slate-500">
            <ViewModeIcon className="w-4 h-4" />
            <span>{viewModeInfo.text}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid - Now Clickable with Active State */}
      <div className="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Total Entries - Clickable */}
          <button
            onClick={() => onStatClick?.('total')}
            className={`text-center rounded-lg p-3 transition-all ${
              isStatActive('total')
                ? 'bg-blue-100 ring-2 ring-blue-500 shadow-md'
                : 'bg-white hover:bg-slate-50 hover:shadow-md'
            }`}
          >
            <div className="text-2xl font-bold text-slate-700 flex items-center justify-center space-x-1">
              <FileText className="w-5 h-5" />
              <span>{timeEntries.length}</span>
            </div>
            <div className="text-sm text-slate-600">Total Entries</div>
          </button>

          {/* Needs Approval (Admin/Manager only) - Clickable */}
          {(userRole === 'admin' || userRole === 'manager') && (
            <button
              onClick={() => onStatClick?.('needs_approval')}
              className={`text-center rounded-lg p-3 transition-all relative ${
                isStatActive('needs_approval')
                  ? 'bg-red-100 ring-2 ring-red-500 shadow-md'
                  : needsApproval > 0
                  ? 'bg-red-50 hover:bg-red-100 hover:shadow-md'
                  : 'bg-white hover:bg-slate-50 hover:shadow-md'
              }`}
            >
              <div className={`text-2xl font-bold flex items-center justify-center space-x-1 ${
                needsApproval > 0 ? 'text-red-600' : 'text-slate-600'
              }`}>
                <AlertCircle className="w-5 h-5" />
                <span>{needsApproval}</span>
              </div>
              <div className="text-sm text-slate-600">Needs Approval</div>
              {needsApproval > 0 && !isStatActive('needs_approval') && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
          )}

      

          {/* Old Drafts (>3 days) - Clickable */}
          <button
            onClick={() => onStatClick?.('old_drafts')}
            className={`text-center rounded-lg p-3 transition-all relative ${
              isStatActive('old_drafts')
                ? 'bg-yellow-100 ring-2 ring-yellow-500 shadow-md'
                : oldDrafts > 0
                ? 'bg-yellow-50 hover:bg-yellow-100 hover:shadow-md'
                : 'bg-white hover:bg-slate-50 hover:shadow-md'
            }`}
          >
            <div className={`text-2xl font-bold flex items-center justify-center space-x-1 ${
              oldDrafts > 0 ? 'text-yellow-600' : 'text-slate-600'
            }`}>
              <AlertCircle className="w-5 h-5" />
              <span>{oldDrafts}</span>
            </div>
            <div className="text-sm text-slate-600">Drafts &gt;3d</div>
            {oldDrafts > 0 && !isStatActive('old_drafts') && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
            )}
          </button>

          {/* Total Drafts - Clickable */}
          <button
            onClick={() => onStatClick?.('drafts')}
            className={`text-center rounded-lg p-3 transition-all ${
              isStatActive('drafts')
                ? 'bg-yellow-100 ring-2 ring-yellow-500 shadow-md'
                : draftEntries > 0
                ? 'bg-yellow-50 hover:bg-yellow-100 hover:shadow-md'
                : 'bg-white hover:bg-slate-50 hover:shadow-md'
            }`}
          >
            <div className={`text-2xl font-bold flex items-center justify-center space-x-1 ${
              draftEntries > 0 ? 'text-yellow-600' : 'text-slate-600'
            }`}>
              <FileText className="w-5 h-5" />
              <span>{draftEntries}</span>
            </div>
            <div className="text-sm text-slate-600">Total Drafts</div>
          </button>

          {/* Approved Entries - Clickable */}
          <button
            onClick={() => onStatClick?.('approved')}
            className={`text-center rounded-lg p-3 transition-all ${
              isStatActive('approved')
                ? 'bg-green-100 ring-2 ring-green-500 shadow-md'
                : approvedEntries > 0
                ? 'bg-green-50 hover:bg-green-100 hover:shadow-md'
                : 'bg-white hover:bg-slate-50 hover:shadow-md'
            }`}
          >
            <div className={`text-2xl font-bold flex items-center justify-center space-x-1 ${
              approvedEntries > 0 ? 'text-green-600' : 'text-slate-600'
            }`}>
              <CheckCircle className="w-5 h-5" />
              <span>{approvedEntries}</span>
            </div>
            <div className="text-sm text-slate-600">Approved</div>
          </button>

          {/* Total Hours - Not clickable, shows filtered time */}
          <div className="text-center bg-white rounded-lg p-3 border-2 border-blue-200">
            <div className="text-2xl font-bold text-blue-600 flex items-center justify-center space-x-1">
              <Clock className="w-5 h-5" />
              <span>{formatTime(totalVisibleTime)}</span>
            </div>
            <div className="text-sm text-slate-600">
              {hasActiveFilters || filters.status !== 'all' || (filters.draftAge && filters.draftAge !== 'all')
                ? 'Hours (filtered)' 
                : 'Total Hours'}
            </div>
          </div>
        </div>

        {/* Additional Context Row */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center space-x-4">
              {visibleEntries.length !== timeEntries.length && (
                <span>Showing {visibleEntries.length} of {timeEntries.length} entries</span>
              )}
              {visibleEntries.length === timeEntries.length && (
                <span>Showing all {timeEntries.length} entries</span>
              )}
              {visibleEntries.length > 0 && (
                <span>
                  Avg: {formatTime(totalVisibleTime / visibleEntries.length)} per entry
                </span>
              )}
            </div>
            
            <div className="text-xs">
              {viewMode === 'list' ? 'All entries' : `${viewMode} view`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedQuickStats;